import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, X, Send, Headphones, Sparkles, CheckCircle, AlertCircle, Mail, Phone, CornerDownLeft } from 'lucide-react';
import { useAuth } from '../../Hooks/useAuth';
import { useLocation } from 'react-router-dom';
import { contactService } from '../../Services';
import { toast } from 'react-toastify';

const QUICK_SUBJECTS = [
  "Assistance technique",
  "Texte juridique",
  "Abonnement",
  "Autre"
];

interface ChatMessage {
  id: string;
  sender: 'bot' | 'user';
  text: string;
  time: string;
  subject?: string;
}

export const FloatingContactChat: React.FC = () => {
  const { isAuthenticated, user: currentUser } = useAuth();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [hasRegisterToast, setHasRegisterToast] = useState(false);

  // Form states
  const [selectedSubject, setSelectedSubject] = useState<string>("Assistance technique");
  const [message, setMessage] = useState<string>("");
  const [guestEmail, setGuestEmail] = useState<string>("");
  const [guestPhone, setGuestPhone] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Conversation history
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 'welcome',
      sender: 'bot',
      text: "Bonjour ! Comment pouvons-nous vous aider ? Posez votre question ou décrivez votre problème ci-dessous, notre équipe vous répondra directement.",
      time: 'À l\'instant'
    }
  ]);

  const chatBottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea up to 110px max height without breaking design
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    const minHeight = 36;
    const maxHeight = 110;
    const nextHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight);
    textarea.style.height = `${nextHeight}px`;
  }, [message]);

  // Listen to register CTA visibility changes
  useEffect(() => {
    const handleRegisterVisible = () => setHasRegisterToast(true);
    const handleRegisterDismissed = () => setHasRegisterToast(false);

    window.addEventListener('docarn-register-cta-visible', handleRegisterVisible);
    window.addEventListener('docarn-register-cta-dismissed', handleRegisterDismissed);

    return () => {
      window.removeEventListener('docarn-register-cta-visible', handleRegisterVisible);
      window.removeEventListener('docarn-register-cta-dismissed', handleRegisterDismissed);
    };
  }, []);

  // Auto-scroll on new message
  useEffect(() => {
    if (isOpen) {
      chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen]);

  // Ne pas afficher sur certaines pages d'authentification
  if (location.pathname.startsWith('/auth/verify')) {
    return null;
  }

  // Positionnement surélevé demandé :
  // Si le toast d'inscription temporaire est affiché : se positionne encore plus haut pour ne jamais chevaucher
  const bottomButtonPos = hasRegisterToast && !isAuthenticated
    ? 'bottom-48 sm:bottom-44 right-4 sm:right-6'
    : 'bottom-8 sm:bottom-12 right-4 sm:right-6';

  const bottomWindowPos = hasRegisterToast && !isAuthenticated
    ? 'bottom-60 sm:bottom-56 right-4 sm:right-6'
    : 'bottom-20 sm:bottom-24 right-4 sm:right-6';

  const handleSendMessage = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setErrorMessage(null);

    const trimmedMsg = message.trim();
    if (!trimmedMsg) return;

    if (!isAuthenticated) {
      const emailTrimmed = guestEmail.trim();
      if (!emailTrimmed) {
        setErrorMessage("Votre e-mail est requis pour vous répondre.");
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(emailTrimmed)) {
        setErrorMessage("Veuillez saisir un e-mail valide.");
        return;
      }
    }

    const now = new Date();
    const timeStr = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;

    // Add user bubble
    const userMsg: ChatMessage = {
      id: String(Date.now()),
      sender: 'user',
      text: trimmedMsg,
      subject: selectedSubject,
      time: timeStr
    };

    setMessages(prev => [...prev, userMsg]);
    setMessage("");
    if (textareaRef.current) {
      textareaRef.current.style.height = '36px';
    }
    setIsSubmitting(true);

    try {
      await contactService.sendSupportMessage({
        fullName: isAuthenticated ? currentUser?.fullName : undefined,
        email: isAuthenticated ? currentUser?.email : guestEmail.trim(),
        phoneNumber: isAuthenticated ? undefined : guestPhone.trim() || undefined,
        subject: selectedSubject,
        message: trimmedMsg
      });

      // Add bot confirmation bubble
      setTimeout(() => {
        setMessages(prev => [
          ...prev,
          {
            id: String(Date.now() + 1),
            sender: 'bot',
            text: `✅ Merci ! Votre demande (${selectedSubject}) a été transmise aux administrateurs. Vous recevrez une réponse par e-mail à l'adresse ${isAuthenticated ? currentUser?.email : guestEmail.trim()}.`,
            time: 'À l\'instant'
          }
        ]);
      }, 500);

      toast.success("Message transmis au support !");
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || "Erreur lors de l'envoi du message.";
      setErrorMessage(msg);
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* ─── FENÊTRE DE CHAT FLOTTANTE (POPOVER AU COIN SANS BACKDROP) ─── */}
      {isOpen && (
        <div 
          className={`fixed ${bottomWindowPos} z-50 w-[calc(100vw-2rem)] sm:w-[360px] max-w-[360px] h-[480px] max-h-[75vh] bg-white rounded-2xl shadow-2xl border border-zinc-200/90 flex flex-col overflow-hidden animate-fade-in transition-all duration-200`}
        >
          {/* Header du Chat */}
          <div className="bg-gradient-to-r from-zinc-950 via-primary-900 to-indigo-950 text-white p-3.5 flex items-center justify-between shrink-0 shadow-sm border-b border-white/10">
            <div className="flex items-center gap-2.5">
              <div className="relative">
                <div className="w-8 h-8 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-amber-300">
                  <Headphones className="w-4 h-4" />
                </div>
                <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-400 border-2 border-zinc-950 rounded-full"></span>
              </div>
              <div className="leading-tight">
                <div className="text-[12px] font-bold text-white flex items-center gap-1.5">
                  <span>Support Direct Docarn</span>
                </div>
                <div className="text-[10px] text-zinc-300 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                  <span>Équipe en ligne &bull; Réponse rapide</span>
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="text-white/70 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
              title="Réduire"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Bandeau d'identité de l'utilisateur (discret, non extravagant) */}
          <div className="px-3 py-1.5 bg-zinc-50 border-b border-zinc-100 flex items-center justify-between text-[10px] text-zinc-600 shrink-0">
            {isAuthenticated && currentUser ? (
              <div className="flex items-center gap-1.5 truncate">
                <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                <span className="font-semibold text-zinc-800 truncate">{currentUser.fullName || currentUser.email}</span>
                <span className="text-zinc-400">({currentUser.cabinetName || 'Abonné'})</span>
              </div>
            ) : (
              <div className="text-zinc-500 flex items-center gap-1 truncate">
                <span>Mode visiteur &bull; Renseignez votre e-mail ci-dessous</span>
              </div>
            )}
          </div>

          {/* Corps de la discussion (Messages) */}
          <div className="flex-1 p-3 overflow-y-auto space-y-3 bg-zinc-50/40 text-[11px]">
            {messages.map((m) => (
              <div
                key={m.id}
                className={`flex flex-col ${m.sender === 'user' ? 'items-end' : 'items-start'}`}
              >
                {m.sender === 'user' && m.subject && (
                  <span className="text-[9px] text-zinc-400 font-medium mb-0.5 px-1">
                    Objet : {m.subject}
                  </span>
                )}
                <div
                  className={`max-w-[88%] sm:max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[11px] sm:text-[11.5px] leading-relaxed shadow-sm break-words [overflow-wrap:anywhere] ${
                    m.sender === 'user'
                      ? 'bg-gradient-to-br from-violet-600 to-indigo-700 text-white rounded-br-xs shadow-violet-900/10'
                      : 'bg-white text-zinc-800 border border-zinc-200/90 rounded-bl-xs'
                  }`}
                >
                  <p className="whitespace-pre-wrap break-words [overflow-wrap:anywhere] leading-relaxed select-text">
                    {m.text}
                  </p>
                </div>
                <span className="text-[9px] text-zinc-400 mt-0.5 px-1">{m.time}</span>
              </div>
            ))}

            {/* Champs invités intégrés à la discussion si non connecté */}
            {!isAuthenticated && (
              <div className="bg-white p-2.5 rounded-xl border border-zinc-200/90 shadow-xs space-y-2 mt-2">
                <div className="text-[10px] font-bold text-zinc-700 flex items-center gap-1">
                  <Mail className="w-3 h-3 text-violet-600" />
                  <span>Votre e-mail pour vous répondre</span>
                </div>
                <input
                  type="email"
                  required
                  placeholder="votre-email@exemple.com *"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  className="w-full px-2.5 py-1.5 text-[11px] border border-zinc-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-violet-600 bg-zinc-50/50"
                />
                <input
                  type="tel"
                  placeholder="Téléphone (optionnel)"
                  value={guestPhone}
                  onChange={(e) => setGuestPhone(e.target.value)}
                  className="w-full px-2.5 py-1.5 text-[11px] border border-zinc-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-violet-600 bg-zinc-50/50"
                />
              </div>
            )}

            {errorMessage && (
              <div className="p-2 bg-red-50 border border-red-200 rounded-lg text-[10px] text-red-700 flex items-center gap-1.5">
                <AlertCircle className="w-3.5 h-3.5 shrink-0 text-red-600" />
                <span>{errorMessage}</span>
              </div>
            )}

            <div ref={chatBottomRef} />
          </div>

          {/* Quick Subject Chips */}
          <div className="px-2.5 py-1.5 bg-white border-t border-zinc-100 flex items-center gap-1 overflow-x-auto shrink-0">
            <span className="text-[9px] font-bold text-zinc-400 uppercase shrink-0">Sujet :</span>
            {QUICK_SUBJECTS.map((subj) => (
              <button
                key={subj}
                type="button"
                onClick={() => setSelectedSubject(subj)}
                className={`px-2 py-0.5 rounded-md text-[10px] font-semibold transition-colors shrink-0 cursor-pointer ${
                  selectedSubject === subj
                    ? 'bg-violet-600 text-white shadow-2xs'
                    : 'bg-zinc-100 hover:bg-zinc-200 text-zinc-600'
                }`}
              >
                {subj}
              </button>
            ))}
          </div>

          {/* Input Footer avec textarea extensible automatiquement (max 110px) et limite 500 car. */}
          <form
            onSubmit={handleSendMessage}
            className="p-2.5 bg-white border-t border-zinc-100 shrink-0 space-y-1.5"
          >
            <div className="flex items-end gap-1.5">
              <div className="flex-1 relative">
                <textarea
                  ref={textareaRef}
                  rows={1}
                  maxLength={500}
                  value={message}
                  onChange={(e) => {
                    const val = e.target.value.slice(0, 500);
                    setMessage(val);
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder="Écrivez votre message (500 car. max)..."
                  className="w-full px-3 py-2 text-[11px] sm:text-[11.5px] border border-zinc-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-600/20 focus:border-violet-600 resize-none overflow-y-auto leading-relaxed transition-[height] duration-75 block shadow-2xs placeholder:text-zinc-400"
                  style={{ minHeight: '38px', maxHeight: '110px' }}
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting || !message.trim()}
                className="w-9 h-9 rounded-xl bg-violet-700 hover:bg-violet-800 text-white flex items-center justify-center shrink-0 transition-all cursor-pointer disabled:opacity-40 shadow-xs mb-0.5"
                title="Envoyer (Entrée)"
              >
                {isSubmitting ? (
                  <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <Send className="w-3.5 h-3.5" />
                )}
              </button>
            </div>

            {/* Compteur de caractères & aide */}
            <div className="flex items-center justify-between px-1 text-[9px] text-zinc-400">
              <span className="hidden sm:inline text-zinc-400">Touche Entrée pour envoyer &bull; Maj+Entrée pour saut de ligne</span>
              <span className="sm:hidden text-zinc-400">Entrée pour envoyer</span>
              <span className={`font-mono transition-colors ${
                message.length >= 480 
                  ? 'text-red-500 font-bold' 
                  : message.length >= 400 
                  ? 'text-amber-500 font-semibold' 
                  : 'text-zinc-400'
              }`}>
                {message.length}/500
              </span>
            </div>
          </form>
        </div>
      )}

      {/* ─── BOUTON FLOTTANT DISCRET ET SURÉLEVÉ ─── */}
      <div 
        className={`fixed ${bottomButtonPos} z-40 transition-all duration-300 ease-out`}
      >
        {/* Message court d'indication sur mobile pour savoir que c'est pour nous contacter */}
        {!isOpen && (
          <div 
            onClick={() => setIsOpen(true)}
            className="sm:hidden absolute -top-7 right-0 flex items-center gap-1 px-2 py-0.5 rounded-full bg-zinc-950/95 text-white text-[9.5px] font-medium shadow-md border border-white/20 whitespace-nowrap cursor-pointer hover:bg-zinc-800 transition-all animate-pulse"
            title="Nous contacter"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
            <span>Nous contacter</span>
            <div className="w-1.5 h-1.5 bg-zinc-950/95 rotate-45 absolute -bottom-0.5 right-3 border-r border-b border-white/20"></div>
          </div>
        )}

        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="group relative flex items-center justify-center gap-2 p-2.5 sm:px-3.5 sm:py-2.5 rounded-full bg-gradient-to-r from-zinc-950 via-primary-900 to-indigo-950 text-white font-semibold text-[11px] shadow-lg shadow-zinc-950/20 hover:shadow-xl hover:scale-105 active:scale-95 transition-all duration-200 border border-white/15 cursor-pointer backdrop-blur-sm"
          title={isOpen ? "Fermer le chat" : "Nous contacter"}
          aria-label={isOpen ? "Fermer le chat" : "Nous contacter"}
          id="btn-floating-direct-contact"
        >
          {/* Témoin discret en ligne */}
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
          </span>

          {isOpen ? (
            <X className="w-4 h-4 text-white" />
          ) : (
            <Headphones className="w-4 h-4 sm:w-3.5 sm:h-3.5 text-amber-300 shrink-0" />
          )}

          {/* Sur petit écran / mobile : seul le casque est affiché. Sur écran >= sm : texte complet */}
          <span className="hidden sm:inline tracking-tight text-white font-medium whitespace-nowrap">
            {isOpen ? 'Fermer le chat' : 'Assistance en ligne'}
          </span>
        </button>
      </div>
    </>
  );
};
